import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RWebShare } from 'react-web-share';
import { useTranslation } from 'next-i18next';
import { useGlobalsService } from '@restworld/data-services';

import { BASE_URL } from '@restworld/utils-common';
import { ButtonProps, Button } from '@restworld/ui-ds-base';
import { shortenOnce } from './short-url-cache';

const text =
  'Hey, ti stavo pensando leggendo questa offerta di lavoro. Apri il link per vedere tutti i dettagli e candidarti!';
const commonString = (ambassadorRefCode: string) =>
  `utm_source=referral&utm_medium=ambassador&utm_campaign=worker_acquisition&ref_code=${ambassadorRefCode}`;

interface IJobShareModalProps extends Omit<ButtonProps, 'title' | 'onClick'> {
  /** Fired when the share action is triggered (not a button DOM event). */
  onClick?: () => void;
  buttonTitle?: string;
  jobPositionId?: string;
  redirectUrl?: string;
  ambassadorRefCode?: string;
  title?: string | null;
  fullwidth?: boolean;
}

export const JobShareModal: React.FunctionComponent<IJobShareModalProps> = ({
  buttonTitle = '',
  jobPositionId = '',
  redirectUrl,
  ambassadorRefCode,
  title,
  variant = 'secondary-neutral',
  onClick,
  onPointerEnter,
  onPointerDown,
  onFocus,
  ...rest
}) => {
  // Keyed by the URL it was produced for, so a component that is reused for another job
  // position never shares the previous one's link while the new one is being fetched.
  const [shortened, setShortened] = useState<{ for: string; url: string } | null>(null);
  // The request is shared across components and outlives any one of them, so a late resolution
  // must not set state on a modal that has already gone.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const globalService = useGlobalsService();
  const url = useMemo(() => {
    if (redirectUrl) {
      if (ambassadorRefCode)
        return `${redirectUrl}${
          ambassadorRefCode ? `${redirectUrl.includes('?') ? `&` : `?`}${commonString(ambassadorRefCode)}` : ``
        }`;
      return redirectUrl;
    }
    if (jobPositionId) {
      if (ambassadorRefCode) return `${BASE_URL}/posizione/${jobPositionId}?${commonString(ambassadorRefCode)}`;
      return `${BASE_URL}/posizione/${jobPositionId}?utm_source=referral`;
    }
    return '';
  }, [ambassadorRefCode, jobPositionId, redirectUrl]);

  /**
   * Shortening a URL is a DynamoDB write, and this used to run from a `useEffect` on mount.
   * A job ad page mounts three of these buttons, so every view of an ad — by anybody, including
   * Googlebot and the scraper fleet that crawls our listings — wrote three rows whether or not
   * a single person ever pressed Share. Measured over 24h on 27-28/08/2026: 19,047 requests
   * carrying 865 distinct URLs, which throttled the table's provisioned write capacity three
   * times and returned 500s.
   *
   * It now runs at most once per URL, on the first signal that the user is heading for the
   * button: hover, pointer down, keyboard focus, or — see below, because this is the one that
   * was missing and cost us the feature on mobile — the first touch on the page.
   *
   * It is deliberately not moved into the click handler. `navigator.share()` — which `RWebShare`
   * calls — needs the user gesture still active, and awaiting a network round-trip first makes
   * Safari reject it. Prefetching keeps the click synchronous.
   *
   * When the request has not come back yet, or failed, we share the long URL. That was already
   * the failure behaviour; it also fixes sharing an empty string, which is what a click before
   * the old effect resolved produced.
   */
  const prefetchShortUrl = useCallback(() => {
    if (!url) return;

    shortenOnce((params) => globalService.createShortenUrl(params), url).then((short) => {
      if (!short || !mounted.current) return;
      // Idempotent: the effect below can call this again for the same URL, and the cache answers
      // from a resolved promise, so a fresh object here would re-render for no change.
      setShortened((previous) =>
        previous?.for === url && previous.url === short ? previous : { for: url, url: short }
      );
    });
  }, [globalService, url]);

  /**
   * A finger has no hover, and the URL it would share is not fixed for the page view. Two
   * separate reasons the share link came out long on a phone; the effect below answers both.
   *
   * The button's own handlers were chosen because they "fire before the click", which is true of a
   * mouse — `pointerover` arrives whole seconds before it — and false of a tap: `pointerdown` and
   * `click` are the two ends of one gesture, roughly 100ms apart, against a round trip measured at
   * ~280ms in production. So on a phone the prefetch lost the race every time and the sheet opened
   * on the long URL, silently: the request is made, it succeeds, the row is written, and the answer
   * arrives after `navigator.share()` has already been handed its URL.
   *
   * The first touch anywhere on the page — a scroll, normally seconds before anyone reaches the
   * button — is the earliest signal that a human on a touch device is here, and #350 hung the
   * prefetch on it. That fixed the plain case and left the ambassadors, who are the reason the
   * feature exists: `ambassadorRefCode` arrives from two chained calls, `/me` then
   * `/ambassador/me`, so their URL starts as the plain one and becomes the one carrying `ref_code`
   * a second or two in. If the first touch has already been spent on the plain URL, `shortened.for`
   * stops matching and the share falls back to the long one. Measured in production on 07/09/2026:
   * the same ad shortened twice in the same second, once without `ref_code` and once with.
   *
   * Hence `touched`: the touch is remembered rather than only listened for, so a change of URL
   * prefetches at once instead of waiting for a touch that — from someone who has finished
   * scrolling and is reaching for Share — never comes.
   *
   * A visitor who never touches still costs nothing, which is the property #327 bought: crawlers
   * do not fire `touchstart`, so `touched` stays false and no request is made.
   */
  const touched = useRef(false);
  useEffect(() => {
    if (!url) return;

    if (touched.current) {
      prefetchShortUrl();
      return;
    }

    const onFirstTouch = () => {
      touched.current = true;
      prefetchShortUrl();
    };
    document.addEventListener('touchstart', onFirstTouch, { once: true, passive: true });
    return () => document.removeEventListener('touchstart', onFirstTouch);
  }, [prefetchShortUrl, url]);

  const shareUrl = shortened?.for === url ? shortened.url : url;

  const { t } = useTranslation('common');
  return (
    <RWebShare
      data={{
        title: `${title ?? t('share_location_with_colleagues')} ;)`,
        text,
        url: shareUrl,
      }}
      onClick={() => {
        onClick && onClick();
      }}
    >
      {/* The three intent handlers are composed, not overridden. They are valid props on this
          component (via ButtonProps), so a caller can pass one; spreading `rest` over them would
          disable the prefetch the share link depends on, and spreading it under them would
          silently drop the caller's handler. Neither is caught at compile time. */}
      <Button
        {...rest}
        variant={variant}
        onPointerEnter={(event) => {
          onPointerEnter?.(event);
          prefetchShortUrl();
        }}
        onPointerDown={(event) => {
          onPointerDown?.(event);
          prefetchShortUrl();
        }}
        onFocus={(event) => {
          onFocus?.(event);
          prefetchShortUrl();
        }}
      >
        {buttonTitle}
      </Button>
    </RWebShare>
  );
};
export default JobShareModal;
